import {Text} from '@/components-v2/atoms'
import {LabelValue} from '@/components-v2/molecules'
import {splitUtcToLocalDate} from '@/helpers/dateUtils'
import {PromotionStep, RewardType} from '@/types/promotion'
import {PromotionType} from '@/utils/constants/promotionConstants'
import {PromotionReviewSection} from './PromotionReviewSection'

/**
 * @param {{
 *  promotionReviewData: import('@/types/promotion').Promotion,
 *  showEdit: boolean,
 *  setCurrentStep: (step: import('@/types/promotion').PromotionStep) => void,
 * }} props
 */
export const PromotionReviewAttributes = ({promotionReviewData, showEdit, setCurrentStep}) => {
  const rewardType = promotionReviewData?.promotionConditions?.[0]?.rewards?.[0]?.type
  const promotionType = promotionReviewData?.promotionType

  const formatReviewDate = rawDate => {
    if (!rawDate) return '-'
    const pickerDate = rawDate.includes('T') ? splitUtcToLocalDate(rawDate) : rawDate
    const [year, month, day] = pickerDate.split('-')
    if (!year || !month || !day) return '-'
    return `${month}/${day}/${year}`
  }

  const getDateRange = () => {
    if (promotionReviewData?.activeStartDate && promotionReviewData?.activeEndDate) {
      return `${formatReviewDate(promotionReviewData.activeStartDate)} - ${formatReviewDate(promotionReviewData.activeEndDate)}`
    }
    return '-'
  }

  const getResetFrequency = () => {
    return promotionReviewData?.resetFrequencyInDays ? `${promotionReviewData.resetFrequencyInDays} Days` : '-'
  }

  const renderAdditionalAttributes = () => {
    switch (rewardType) {
      case RewardType.FREE_PRODUCT:
      case RewardType.GIFT_ITEM:
        return (
          <LabelValue
            keyName="Max Free Items per Order"
            val={promotionReviewData?.maxFreeItemsPerOrder?.toString() || '-'}
          />
        )
      case RewardType.POINTS:
        return (
          <LabelValue
            keyName="Max Times per Order"
            val={promotionReviewData?.maxTimesPerOrder?.toString() || '-'}
          />
        )
      default:
        return (
          <LabelValue
            keyName="Max Allowed Percentage"
            val={promotionReviewData?.maxAllowedPercentage?.toString() || '-'}
          />
        )
    }
  }

  return (
    <PromotionReviewSection
      label="Attributes"
      showEdit={showEdit}
      onEdit={() => setCurrentStep(PromotionStep.ATTRIBUTES)}
    >
      <div className="flex w-full flex-col gap-6 overflow-hidden">
        {/* Availability */}
        <div>
          <Text size="small" variant="bold">
            Availability
          </Text>
          <div className="mt-2 h-px bg-[var(--secondary-800)]" />
          <div className="grid grid-cols-3 gap-x-4">
            <LabelValue keyName="Date" val={getDateRange()} />
            <LabelValue keyName="Active" val={promotionReviewData?.active ? 'Yes' : 'No'} />
          </div>
        </div>

        {/* Additional Attributes */}
        <div>
          <Text size="small" variant="bold">
            Additional Attributes
          </Text>
          <div className="mt-2 h-px bg-[var(--secondary-800)]" />
          <div className="grid grid-cols-3 gap-x-4">
            {renderAdditionalAttributes()}
            <LabelValue
              keyName="Max Allowed Order per Store"
              val={promotionReviewData?.maxAllowedOrderPerStore?.toString() || '-'}
            />
            {promotionType !== PromotionType.WELCOME_DISCOUNT && (
              <>
                <LabelValue keyName="Reset Frequency" val={getResetFrequency()} />
                <LabelValue keyName="Behavior" val={promotionReviewData?.behavior || '-'} />
              </>
            )}
          </div>
        </div>
      </div>
    </PromotionReviewSection>
  )
}
